import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import { isAndroid, isiOS, isMobileEmbedded } from '@rover/rsdk/src/modules/Network/userAgent';

declare global {
  interface Window {
    ReactNativeWebView: any;
  }
}

type EventNames = {
  facebook_event: string;
  kochava_event: string;
  firebase_event?: string;
  branch_event?: string;
};
type EventData = {
  [key: string]: number | boolean | string;
};
type Event = {
  eventData: EventData;
  eventNames: EventNames;
};
type Message = {
  action: 'event';
} & EventData;
type PostMessagePayload = Message | string;
type PostMessage = (arg0: PostMessagePayload) => void;
type RoverMessageHandlerContext = {
  postMessage: PostMessage;
};

// Use this to give errors from this function a uniform format and something we can easily search for
const reportEmbeddedError = (message: string): false => {
  captureError(new Error(`FireEmbeddedWebEventError: ${message}`));
  return false;
};

/* eslint-disable rover/no-platform-specific-globals-or-imports */
export const isReactNativeWebView = () =>
  typeof window !== 'undefined' && !!window.ReactNativeWebView;

const getReactNativeRover = (): RoverMessageHandlerContext =>
  typeof window !== 'undefined' && window.ReactNativeWebView;

const getAndroidRover = (): RoverMessageHandlerContext =>
  typeof window !== 'undefined' && window.rover;

const getiOSRover = (): RoverMessageHandlerContext =>
  typeof window !== 'undefined' &&
  window.webkit &&
  window.webkit.messageHandlers &&
  window.webkit.messageHandlers.rover;
/* eslint-enable rover/no-platform-specific-globals-or-imports */

const Platforms = Object.freeze({
  android: 'android',
  ios: 'ios',
  reactNative: 'react-native',
});

type PlatformsKeys = keyof typeof Platforms;

type Platform = (typeof Platforms)[PlatformsKeys];

const getPlatform = (): Platform | null | undefined => {
  if (isReactNativeWebView()) {
    return Platforms.reactNative;
  }

  if (isAndroid()) {
    return Platforms.android;
  }

  if (isiOS()) {
    return Platforms.ios;
  }

  return null;
};

const getRover = (platform: Platform): RoverMessageHandlerContext | null | undefined => {
  switch (platform) {
    case Platforms.reactNative:
      return getReactNativeRover();

    case Platforms.android:
      return getAndroidRover();

    case Platforms.ios:
      return getiOSRover();

    default:
      return null;
  }
};

const prepareEventData = (platform: Platform, message: Message): PostMessagePayload => {
  switch (platform) {
    case Platforms.reactNative:
    case Platforms.android:
      return JSON.stringify(message);

    case Platforms.ios:
    default:
      return message;
  }
};

export const fireEmbeddedWebEvent = ({ eventNames, eventData }: Event): boolean => {
  if (!isMobileEmbedded()) {
    return reportEmbeddedError(`"${fireEmbeddedWebEvent.name}" called outside embedded context`);
  }

  const platform = getPlatform();

  if (!platform) {
    return reportEmbeddedError('Unable to determine platform');
  }

  const rover = getRover(platform);

  if (!rover) {
    return reportEmbeddedError(
      `Unable to find embedded context object "rover" for platform: ${platform}`
    );
  }

  if (!rover.postMessage) {
    return reportEmbeddedError(
      `Embedded context did not have "postMessage" function for platform: ${platform}`
    );
  }

  try {
    // Call postMessage off the `rover` object so that on android it doesn't lose the `this`
    // and we can avoid having postMessage.bind(rover)
    rover.postMessage(
      prepareEventData(platform, {
        action: 'event',
        ...eventNames,
        ...eventData,
      })
    );
  } catch (err) {
    return reportEmbeddedError(`Error firing embedded web event: ${err}`);
  }

  return true;
};

/** Action name the native WebView host matches on to save + share a generated file. */
export const EMBEDDED_FILE_DOWNLOAD_ACTION = 'download_file';

export type EmbeddedFileDownload = {
  filename: string;
  mimeType: string;
  contents: string;
};

export const fireEmbeddedWebEventWithProperties = (eventName, eventData): boolean => {
  if (!isMobileEmbedded()) {
    return reportEmbeddedError(`"${fireEmbeddedWebEvent.name}" called outside embedded context`);
  }

  const platform = getPlatform();

  if (!platform) {
    return reportEmbeddedError('Unable to determine platform');
  }

  const rover = getRover(platform);

  if (!rover) {
    return reportEmbeddedError(
      `Unable to find embedded context object "rover" for platform: ${platform}`
    );
  }

  if (!rover.postMessage) {
    return reportEmbeddedError(
      `Embedded context did not have "postMessage" function for platform: ${platform}`
    );
  }

  try {
    // Call postMessage off the `rover` object so that on android it doesn't lose the `this`
    // and we can avoid having postMessage.bind(rover)
    rover.postMessage(
      prepareEventData(platform, {
        action: eventName,
        ...eventData,
      })
    );
  } catch (err) {
    return reportEmbeddedError(`Error firing embedded web event: ${err}`);
  }

  return true;
};

/**
 * Asks the native app to write a generated file to disk and open the OS share
 * sheet for it. The embedded WebView has no download delegate, so the Blob URL
 * + anchor click that drives browser downloads silently does nothing there.
 *
 * Only the React Native WebView host implements this action, so callers get
 * `false` everywhere else and can fall back to the browser download path.
 */
export const requestEmbeddedFileDownload = (file: EmbeddedFileDownload): boolean => {
  if (!isReactNativeWebView()) {
    return false;
  }

  return fireEmbeddedWebEventWithProperties(EMBEDDED_FILE_DOWNLOAD_ACTION, file);
};

/** Action name the React Native WebView host matches on to open a client's relationship screen. */
export const EMBEDDED_OPEN_RELATIONSHIP_ACTION = 'open_relationship';

/**
 * Global a React Native WebView host sets before the page loads when it handles
 * `open_relationship`. App builds without the handler would drop the message.
 */
export const EMBEDDED_OPEN_RELATIONSHIP_SUPPORTED = 'roverCanOpenRelationship';

/**
 * Asks the React Native WebView host to open the relationship screen for `opk`. Returns
 * `false` when the host doesn't handle it, so callers can fall back to a link.
 */
export const requestEmbeddedRelationshipOpen = (opk: string): boolean => {
  if (
    !isReactNativeWebView() ||
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    (window as unknown as Record<string, unknown>)[EMBEDDED_OPEN_RELATIONSHIP_SUPPORTED] !== true
  ) {
    return false;
  }

  return fireEmbeddedWebEventWithProperties(EMBEDDED_OPEN_RELATIONSHIP_ACTION, { opk });
};

export default fireEmbeddedWebEvent;
